/** The per-product settings, GENERATED from the registry (plan §3.3.2, §3.9): one row per
 *  product-scope key that applies to the product, one table per group for its option-scope
 *  keys (a column per key, a row per option). Each value shows its effective value and where
 *  it comes from (ops / tenant default / restech default / inferred); an ops override can be
 *  reset to the default. A registry key this console has never heard of renders like any
 *  other — the only keys with a control of their own are `tour.name` + `tour.name_template`,
 *  edited together with the live preview. */
import ReplayIcon from '@mui/icons-material/Replay';
import UndoIcon from '@mui/icons-material/Undo';
import {
  Box,
  Chip,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

import type { CatalogueProduct, SettingSpec, SettingValue } from '@/api/catalogue';
import { FieldHelp } from '@/components/FieldHelp';

import {
  defaultLabel,
  draftGet,
  draftHas,
  fieldKey,
  groupedSpecs,
  KEY,
  optionLanguages,
  type SettingsDraft,
} from './catalogueModel';
import type { ProductPickerContext } from './CatalogueProductControl';
import { CATALOGUE_STORY } from './help';
import { NameControl } from './NameControl';
import { SettingControl, SourceChip } from './SettingControl';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace';

export interface SettingsFormProps {
  tenantId: number;
  product: CatalogueProduct;
  specs: readonly SettingSpec[];
  draft: SettingsDraft;
  /** Set a value (`null` = reset to the default on save). */
  onSet: (key: string, optionId: string | null, value: unknown) => void;
  /** Drop a pending change. */
  onUndo: (key: string, optionId: string | null) => void;
  errors: Record<string, string>;
  /** Rendered after a group's rows (the package composition under "Package"). */
  groupExtras?: Record<string, ReactNode>;
  /** Rendered under a key's row, full width (the join's ownership choices under `tour.join`). */
  rowExtras?: Record<string, ReactNode>;
  /** Where a `catalogue_product` value is picked from. */
  products?: ProductPickerContext;
  disabled?: boolean;
}

/** What a row shows: the pending value, else the effective one. */
function shown(draft: SettingsDraft, key: string, optionId: string | null, effective: SettingValue | undefined) {
  const pending = draftHas(draft, key, optionId);
  const value = pending ? draftGet(draft, key, optionId) : effective?.value;
  return { pending, resetting: pending && value === null, value: pending && value === null ? effective?.value : value };
}

function RowActions({
  pending,
  source,
  label,
  onReset,
  onUndo,
  disabled,
}: {
  pending: boolean;
  source: string | undefined;
  label: string;
  onReset: () => void;
  onUndo: () => void;
  disabled?: boolean;
}) {
  if (pending) {
    return (
      <Tooltip title="Undo this change">
        <IconButton size="small" onClick={onUndo} aria-label={`Undo ${label}`} disabled={disabled}>
          <UndoIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }
  if (source === 'ops') {
    return (
      <Tooltip title="Reset to the default (drop the ops override)">
        <IconButton size="small" onClick={onReset} aria-label={`Reset ${label} to default`} disabled={disabled}>
          <ReplayIcon fontSize="small" />
        </IconButton>
      </Tooltip>
    );
  }
  return <Box sx={{ width: 30 }} />;
}

export function SettingsForm({
  tenantId,
  product,
  specs,
  draft,
  onSet,
  onUndo,
  errors,
  groupExtras = {},
  rowExtras = {},
  products,
  disabled,
}: SettingsFormProps) {
  const effective = product.resolution.product;
  const optionValues = product.resolution.options;
  const languages = optionLanguages(product);
  const byKey = new Map(specs.map((s) => [s.key, s]));

  return (
    <Box>
      {groupedSpecs(specs).map((group) => {
        const productSpecs = group.specs.filter(
          (s) => s.scope === 'product' && s.key in effective && s.key !== KEY.nameTemplate,
        );
        const optionSpecs = group.specs.filter((s) => s.scope === 'option');
        const optionsWithValues = product.options.filter((o) => optionValues[o.id]);
        const optionColumns = optionSpecs.filter((s) => optionsWithValues.some((o) => s.key in (optionValues[o.id] ?? {})));
        if (!productSpecs.length && !optionColumns.length && !groupExtras[group.group]) return null;
        return (
          <Box key={group.group} component="section" sx={{ mb: 2.5 }} aria-label={group.label}>
            <Typography
              variant="overline"
              component="h3"
              sx={{ display: 'block', color: 'text.secondary', fontWeight: 700, letterSpacing: '0.08em', mb: 0.5 }}
            >
              {group.label}
            </Typography>

            {productSpecs.map((spec) => {
              const eff = effective[spec.key];
              const s = shown(draft, spec.key, null, eff);
              const fk = fieldKey(spec.key);
              const isName = spec.key === KEY.name;
              const templateSpec = byKey.get(KEY.nameTemplate);
              const tmpl = templateSpec ? shown(draft, KEY.nameTemplate, null, effective[KEY.nameTemplate]) : null;
              const grain = shown(draft, KEY.grain, null, effective[KEY.grain]);
              return (
                <Box
                  key={spec.key}
                  data-setting={spec.key}
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: '200px 1fr auto' },
                    columnGap: 1.5,
                    rowGap: 0.5,
                    alignItems: 'start',
                    py: 0.75,
                    borderBottom: 1,
                    borderColor: 'divider',
                    ...(s.pending ? { bgcolor: 'rgba(237, 108, 2, 0.06)' } : {}),
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, pt: 0.75, minWidth: 0 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {spec.label}
                    </Typography>
                    <FieldHelp help={{ title: spec.label, body: spec.help, story: CATALOGUE_STORY }} />
                  </Box>
                  <Box sx={{ minWidth: 0 }}>
                    {isName ? (
                      <NameControl
                        tenantId={tenantId}
                        productId={product.id}
                        modeSpec={spec}
                        templateSpec={templateSpec}
                        mode={s.value}
                        template={tmpl?.value}
                        grain={grain.value}
                        options={product.options}
                        onModeChange={(v) => onSet(spec.key, null, v)}
                        onTemplateChange={(v) => onSet(KEY.nameTemplate, null, v)}
                        modeError={errors[fk]}
                        templateError={errors[fieldKey(KEY.nameTemplate)]}
                        disabled={disabled || s.resetting}
                      />
                    ) : (
                      <SettingControl
                        spec={spec}
                        value={s.value}
                        onChange={(v) => onSet(spec.key, null, v)}
                        options={product.options}
                        languageSuggestions={languages}
                        products={products}
                        error={errors[fk]}
                        disabled={disabled || s.resetting}
                      />
                    )}
                    <Typography variant="caption" color="text.secondary" component="div" sx={{ mt: 0.25 }}>
                      {s.resetting
                        ? `Resets to the default on save (${defaultLabel(spec)}).`
                        : `Default: ${defaultLabel(spec)}${spec.applies_rule ? ` · ${spec.applies_rule}` : ''}`}
                      {spec.kind === 'fact' ? ' · a fact triage infers; correct it when it is wrong' : ''}
                    </Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, pt: 0.75, justifySelf: 'end' }}>
                    {s.pending ? (
                      <Chip size="small" label={s.resetting ? 'reset' : 'changed'} color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                    ) : (
                      <SourceChip source={eff?.source} />
                    )}
                    <RowActions
                      pending={s.pending || (isName && !!tmpl?.pending)}
                      source={eff?.source}
                      label={spec.label}
                      disabled={disabled}
                      onReset={() => {
                        onSet(spec.key, null, null);
                        // The mode and its template are one decision: reset them together.
                        if (isName && effective[KEY.nameTemplate]?.source === 'ops') onSet(KEY.nameTemplate, null, null);
                      }}
                      onUndo={() => {
                        onUndo(spec.key, null);
                        if (isName) onUndo(KEY.nameTemplate, null);
                      }}
                    />
                  </Box>
                  {rowExtras[spec.key] && <Box sx={{ gridColumn: '1 / -1', minWidth: 0 }}>{rowExtras[spec.key]}</Box>}
                </Box>
              );
            })}

            {optionColumns.length > 0 && (
              <Box sx={{ overflowX: 'auto', mt: 1 }}>
                <Table size="small" aria-label={`${group.label}: per option`}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Option</TableCell>
                      {optionColumns.map((spec) => (
                        <TableCell key={spec.key}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
                            {spec.label}
                            <FieldHelp help={{ title: spec.label, body: spec.help, story: CATALOGUE_STORY }} />
                          </Box>
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {optionsWithValues.map((o) => (
                      <TableRow key={o.id}>
                        <TableCell sx={{ verticalAlign: 'top', minWidth: 160 }}>
                          <Typography variant="body2">{o.title || (o.is_default ? 'DEFAULT' : o.id)}</Typography>
                          <Typography variant="caption" sx={{ fontFamily: MONO, color: 'text.secondary', display: 'block' }}>
                            {o.id}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {[o.language, (o.start_times ?? []).join(', ')].filter(Boolean).join(' · ')}
                          </Typography>
                        </TableCell>
                        {optionColumns.map((spec) => {
                          const eff = optionValues[o.id]?.[spec.key];
                          const s = shown(draft, spec.key, o.id, eff);
                          const label = `${spec.label} of ${o.title || o.id}`;
                          return (
                            <TableCell key={spec.key} sx={{ verticalAlign: 'top', minWidth: 170, ...(s.pending ? { bgcolor: 'rgba(237, 108, 2, 0.06)' } : {}) }}>
                              <SettingControl
                                spec={spec}
                                value={s.value}
                                onChange={(v) => onSet(spec.key, o.id, v)}
                                ariaLabel={label}
                                error={errors[fieldKey(spec.key, o.id)]}
                                disabled={disabled || s.resetting}
                                fullWidth={false}
                              />
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                {s.pending ? (
                                  <Chip size="small" label={s.resetting ? 'reset' : 'changed'} color="warning" variant="outlined" sx={{ height: 20, fontSize: 11 }} />
                                ) : (
                                  <SourceChip source={eff?.source} />
                                )}
                                <RowActions
                                  pending={s.pending}
                                  source={eff?.source}
                                  label={label}
                                  disabled={disabled}
                                  onReset={() => onSet(spec.key, o.id, null)}
                                  onUndo={() => onUndo(spec.key, o.id)}
                                />
                              </Box>
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}
            {groupExtras[group.group]}
          </Box>
        );
      })}
    </Box>
  );
}
